import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import "./reviews.css";
import { getTeamInfo, listDeleted, listReviewsInfo, manage, myReviewsLink, type ReviewSummary } from "./api";
import { TYPE_LABEL, ThemeIcon, ago, useReviewTheme } from "./shell";
import { docTag, topLevel } from "./content";

// RV-1 — the Review Center: every review waiting on you, when it came in, and basic management
// (open, rename, mark done / reopen, delete → Recently deleted for 30 days with Restore).
// SH-6: reviews a teammate shared with you sit under "Shared with me" and can only be opened.

type Tab = "open" | "done" | "deleted" | "shared";
const LEVEL_LABEL = { comment: "Can comment", view: "Can view" } as const;
const sharedIn = (r: ReviewSummary) => !!r.access && r.access.level !== "owner";
// NR-2: a draft opens back in the builder; anything else opens its room
const openPath = (r: ReviewSummary) => `/reviews/${r.id}${r.draft ? "/edit" : ""}`;

export function ReviewRows({ reviews, tab, activeId, onChanged, compact = false }: { reviews: ReviewSummary[]; tab: Tab; activeId?: string; onChanged: (msg?: string, undo?: () => void) => void; compact?: boolean }) {
  const navigate = useNavigate();
  const [menu, setMenu] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [err, setErr] = useState("");

  const act = async (r: ReviewSummary, action: "rename" | "done" | "reopen" | "delete" | "restore" | "purge", title?: string) => {
    setErr("");
    try {
      await manage(r.id, { action, title });
      setMenu(null); setRenaming(null); setConfirm(null);
      if (action === "delete") onChanged(`Moved “${r.title}” to Recently deleted`, () => manage(r.id, { action: "restore" }).then(() => onChanged()));
      else if (action === "purge") onChanged(`Deleted “${r.title}” for good`);
      else if (action === "restore") onChanged(`Restored “${r.title}”`);
      else onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't do that. Try again.");
    }
  };

  return (
    <div className="lr-list">
      {err && <div className="lr-error">{err}</div>}
      {(tab === "deleted" ? reviews : topLevel(reviews)).map((r) => {
        const pct = r.items ? Math.round((r.decided / r.items) * 100) : 0;
        const tag = tab !== "deleted" ? docTag(r) : null;
        const level = sharedIn(r) ? LEVEL_LABEL[r.access!.level as "comment" | "view"] : null;
        return (
          <div className={`lr-row ${r.id === activeId ? "active" : ""}`} key={r.id}>
            {renaming?.id === r.id ? (
              <form className="lr-rename" style={{ flex: 1 }} onSubmit={(e) => { e.preventDefault(); act(r, "rename", renaming.title); }}>
                <input id={`rn-${r.id}`} aria-label="Review name" autoFocus value={renaming.title} onChange={(e) => setRenaming({ id: r.id, title: e.target.value })} />
                <button type="submit">Save</button>
                <button type="button" onClick={() => setRenaming(null)} style={{ background: "var(--band)", color: "var(--ink)" }}>Cancel</button>
              </form>
            ) : (
              <>
                <button className="main" onClick={() => tab !== "deleted" && navigate(openPath(r))} disabled={tab === "deleted"}>
                  <span className="t">{r.title}</span>
                  <span className="s">
                    {[r.client, r.sender && `from ${r.sender}`, tab === "deleted" ? `deleted ${ago(r.deletedAt)} · gone ${r.purgeOn ? new Date(r.purgeOn).toLocaleDateString([], { month: "short", day: "numeric" }) : "in 30 days"}` : `came in ${ago(r.receivedAt)}`].filter(Boolean).join(" · ")}
                  </span>
                  {!compact && tab !== "deleted" && (
                    <span className="s" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span className="lr-type">{TYPE_LABEL[r.type] || r.type}</span>
                      <span>{r.decided} of {r.items} decided</span>
                    </span>
                  )}
                  {r.draft && tab !== "deleted" && <span className="s"><span className="lr-drafttag">Draft</span></span>}
                  {r.needsYou && tab !== "deleted" && <span className="s"><span className="lr-needstag">Needs you</span></span>}
                  {r.dueLabel && tab !== "deleted" && <span className="s"><span className={r.dueLabel === "overdue" ? "lr-needstag" : "lr-sharedtag"}>{r.dueLabel === "overdue" ? "Overdue" : "Due today"}</span></span>}
                  {tag && <span className="s lr-doctag">{tag}</span>}
                  {level && <span className="s"><span className="lr-sharedtag">{level}</span></span>}
                  {tab !== "deleted" && <span className="bar"><i style={{ width: `${pct}%` }} /></span>}
                </button>
                {tab === "deleted" ? (
                  confirm === r.id ? (
                    <span className="lr-confirm"><span>Delete for good?</span><button className="yes" onClick={() => act(r, "purge")}>Delete</button><button className="no" onClick={() => setConfirm(null)}>Keep</button></span>
                  ) : (
                    <span className="lr-confirm"><button className="lr-pill" onClick={() => act(r, "restore")}>Restore</button><button className="lr-pill" style={{ color: "#dc2626" }} onClick={() => setConfirm(r.id)}>Delete now</button></span>
                  )
                ) : (
                  <button className="more" aria-label={`Options for ${r.title}`} aria-expanded={menu === r.id} onClick={() => setMenu(menu === r.id ? null : r.id)}>⋯</button>
                )}
                {menu === r.id && (
                  <div className="lr-menu" role="menu">
                    <button role="menuitem" onClick={() => navigate(openPath(r))}>Open</button>
                    {!level && <>
                    <button role="menuitem" onClick={() => { setRenaming({ id: r.id, title: r.title }); setMenu(null); }}>Rename</button>
                    {r.status === "done" ? <button role="menuitem" onClick={() => act(r, "reopen")}>Reopen</button> : <button role="menuitem" onClick={() => act(r, "done")}>Mark done</button>}
                    <button role="menuitem" className="danger" onClick={() => act(r, "delete")}>Delete</button>
                    </>}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ReviewCenter() {
  const [theme, toggleTheme] = useReviewTheme();
  const [tab, setTab] = useState<Tab>("open");
  const [reviews, setReviews] = useState<ReviewSummary[] | null>(null);
  const [deleted, setDeleted] = useState<ReviewSummary[]>([]);
  const [owner, setOwner] = useState<boolean | undefined>(undefined);
  const [canBuild, setCanBuild] = useState(false); // NR-2: teammates build reviews; invited clients don't
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ msg: string; undo?: () => void } | null>(null);
  // RV-4 — the "My reviews" phone link
  const [phone, setPhone] = useState<{ token: string | null; busy: boolean } | null>(null);
  const phoneUrl = (t: string) => `${window.location.origin}/my/${t}`;
  const getPhone = async (off = false) => {
    setPhone((p) => ({ token: p?.token || null, busy: true }));
    try { const { token } = await myReviewsLink(off); setPhone({ token, busy: false }); if (off) setToast({ msg: "Phone link turned off. It no longer opens your reviews." }); }
    catch (e) { setPhone(null); setToast({ msg: e instanceof Error ? e.message : "Couldn't make the phone link." }); }
  };

  const load = useCallback(async () => {
    try {
      // Recently deleted failing on its own shouldn't blank the main list
      const [info, d] = await Promise.all([listReviewsInfo(), listDeleted().catch(() => [] as ReviewSummary[])]);
      setReviews(info.reviews); setOwner(info.owner); setDeleted(d); setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load reviews.");
      setReviews([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { Promise.resolve().then(() => getTeamInfo()).then((t) => setCanBuild(!!t?.me?.canBuild), () => setCanBuild(false)); }, []);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 6000); return () => clearTimeout(t); }, [toast]);

  // a document room linked from an open review sits under that review, not as its own row
  const mine = topLevel(reviews || []).filter((r) => !sharedIn(r));
  const open = mine.filter((r) => r.status === "open");
  const done = mine.filter((r) => r.status === "done");
  const shared = topLevel(reviews || []).filter(sharedIn);
  // the tab shows for teammates always, and for the owner only if something reaches them by sharing
  const showShared = owner === false || shared.length > 0;
  const shown = tab === "open" ? open : tab === "done" ? done : tab === "shared" ? shared : deleted;
  const waiting = open.reduce((n, r) => n + (r.items - r.decided), 0);

  return (
    <div className="lr" data-theme={theme}>
      <header className="lr-hdr">
        <Link className="back" to="/" aria-label="Back to Launch OS">‹</Link>
        <div className="grow">
          <h1>Review Center</h1>
          <div className="sub">{reviews === null ? "Loading…" : <><b>{open.length}</b> open · {waiting} items waiting on you</>}</div>
        </div>
        {canBuild && <Link className="lr-newbtn" to="/reviews/new">New review</Link>}
        {owner !== false && <button className="lr-sharebtn" onClick={() => getPhone(false)}>Phone link</button>}
        <button className="icon" onClick={toggleTheme} aria-label={theme === "dark" ? "Light mode" : "Dark mode"}><ThemeIcon theme={theme} /></button>
      </header>
      <nav className="lr-tabs" aria-label="Show">
        <button aria-pressed={tab === "open"} onClick={() => setTab("open")}>Open · {open.length}</button>
        <button aria-pressed={tab === "done"} onClick={() => setTab("done")}>Done · {done.length}</button>
        {showShared && <button aria-pressed={tab === "shared"} onClick={() => setTab("shared")}>Shared with me · {shared.length}</button>}
        <button aria-pressed={tab === "deleted"} onClick={() => setTab("deleted")}>Recently deleted · {deleted.length}</button>
      </nav>
      <main className="lr-scroll">
        {error && <div className="lr-error">{error}</div>}
        {reviews !== null && !shown.length && (
          <div className="lr-empty">
            {tab === "open" && <><b>Nothing waiting on you.</b><span>{owner === false ? `Reviews you make show up here.${canBuild ? " Click New review to build one." : ""} Ones a teammate shares with you are under Shared with me.` : "New reviews show up here. Click New review, or ask Claude to “grab my review requests” or “build a review from these files.”"}</span></>}
            {tab === "shared" && <><b>Nothing shared with you yet.</b><span>When a teammate shares a review with you, it shows up here.</span></>}
            {tab === "done" && <><b>No finished reviews yet.</b><span>Mark a review done from its ⋯ menu.</span></>}
            {tab === "deleted" && <><b>Nothing in Recently deleted.</b><span>Deleted reviews stay here for 30 days, so you can restore them.</span></>}
          </div>
        )}
        <ReviewRows reviews={shown} tab={tab} onChanged={(msg, undo) => { load(); if (msg) setToast({ msg, undo }); }} />
      </main>
      {phone && (
        <div className="lr-ovl" onClick={(e) => e.target === e.currentTarget && setPhone(null)}>
          <div className="lr-tray lr-sharetray" role="dialog" aria-label="Phone link">
            <div className="top"><b>Your reviews on your phone</b><button onClick={() => setPhone(null)} aria-label="Close">✕</button></div>
            <p>Open this link on your iPhone in Safari, tap Share, then <b>Add to Home Screen</b>. It opens full screen and always shows your current reviews, with no sign-in. Keep it private: it opens all your reviews.</p>
            {phone.token ? (
              <>
                <div className="url"><input readOnly value={phoneUrl(phone.token)} onFocus={(e) => e.currentTarget.select()} aria-label="Phone link" /><button className="okb" onClick={() => navigator.clipboard?.writeText(phoneUrl(phone.token!)).then(() => setToast({ msg: "Link copied" }), () => setToast({ msg: "Select the link to copy it" }))}>Copy</button></div>
                <button className="cancel off" disabled={phone.busy} onClick={() => getPhone(true)}>Turn this link off</button>
              </>
            ) : <p>One moment…</p>}
          </div>
        </div>
      )}
      {toast && (
        <div className="lr-toast" role="status">
          <span>{toast.msg}</span>
          {toast.undo && <button onClick={() => { toast.undo!(); setToast(null); }}>Undo</button>}
        </div>
      )}
    </div>
  );
}
